<div class="flex flex-col flex-1">
  <div class="flex items-center mb-2">
    <div>
      <h2 class="text-base font-semibold">Personal Tokens</h2>
      <p class="text-xs text-gray-500">
        Long-lived API tokens that act as
        <b>{{ email || 'you' }}</b>
        . Use them to call Tracer from scripts, CI or curl without signing in.
        <a
          [href]="docsUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-primary underline inline-flex items-center gap-1 ml-1"
        >
          API reference
          <i class="ri-external-link-line"></i>
        </a>
      </p>
    </div>
    <div class="ml-auto flex gap-2">
      <button class="btn btn-sm btn-outline btn-info" (click)="refresh()" [disabled]="loading">
        <i class="ri-refresh-line"></i>
        Refresh
      </button>
      <button class="btn btn-sm btn-primary" (click)="startCreate()" [disabled]="showCreateForm">
        <i class="ri-add-line"></i>
        New token
      </button>
    </div>
  </div>

  @if (listError) {
    <div role="alert" class="alert alert-error text-sm mb-2">
      <i class="ri-error-warning-line"></i>
      <span>{{ listError }}</span>
    </div>
  }

  <!-- ONE-TIME REVEAL. The only moment this value exists outside the
       caller's clipboard — the backend keeps a hash, not the secret. -->
  @if (justCreated) {
    <div class="border-2 border-primary/40 bg-primary/5 rounded-md p-4 mb-3">
      <div class="flex items-start gap-2">
        <i class="ri-key-2-line text-primary text-lg mt-0.5"></i>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold">
            Copy your new token now — this is the only time it will be shown.
          </p>
          <p class="text-xs text-gray-600 mt-0.5">
            Tracer stores only a hash of it. If you lose it, delete this token and create a new one.
          </p>

          <div class="flex items-center gap-2 mt-3">
            <code
              class="flex-1 min-w-0 truncate bg-white border border-gray-200 rounded px-3 py-2 text-sm font-mono select-all"
              [title]="justCreated.token"
            >
              {{ justCreated.token }}
            </code>
            <button class="btn btn-sm btn-primary" (click)="copyToken()">
              @if (copied) {
                <i class="ri-check-line"></i>
                Copied
              } @else {
                <i class="ri-file-copy-line"></i>
                Copy
              }
            </button>
          </div>

          <p class="text-xs text-gray-500 mt-3 mb-1">Try it:</p>
          <code
            class="block bg-gray-900 text-gray-100 rounded px-3 py-2 text-xs font-mono overflow-x-auto whitespace-pre select-all"
          >
            {{ curlExample }}
          </code>

          <p class="text-xs text-gray-500 mt-2">
            Every endpoint this token can reach is listed in the
            <a
              [href]="docsUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-primary underline inline-flex items-center gap-1"
            >
              API reference
              <i class="ri-external-link-line"></i>
            </a>
            .
          </p>

          <button class="btn btn-xs btn-ghost mt-3" (click)="dismissReveal()">
            <i class="ri-close-line"></i>
            I've saved it — hide
          </button>
        </div>
      </div>
    </div>
  }

  <!-- CREATE FORM -->
  @if (showCreateForm) {
    <div class="bg-white shadow-sm rounded-md p-4 mb-3">
      @if (createError) {
        <div role="alert" class="alert alert-error text-sm mb-3">
          <i class="ri-error-warning-line"></i>
          <span>{{ createError }}</span>
        </div>
      }

      <label class="text-sm font-medium text-gray-700" for="token-name">Token name</label>
      <input
        id="token-name"
        class="mt-1 w-full pl-3 pr-3 py-1 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
        [(ngModel)]="newName"
        placeholder="e.g. CI pipeline, local scripts, Grafana probe"
        maxlength="100"
        [disabled]="creating"
        (keyup.enter)="create()"
      />
      <p class="text-xs text-gray-400 mt-1">
        Only for you to recognise the token later — it has no effect on what the token can do.
      </p>

      <div class="flex gap-2 mt-3">
        <button class="btn btn-sm btn-primary" (click)="create()" [disabled]="creating">
          @if (creating) {
            <span class="loading loading-spinner loading-xs"></span>
          }
          Create token
        </button>
        <button class="btn btn-sm btn-ghost" (click)="cancelCreate()" [disabled]="creating">
          Cancel
        </button>
      </div>
    </div>
  }

  <!-- TOKEN LIST -->
  <div class="bg-white shadow-sm rounded-md overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 text-sm">
      <thead class="bg-gray-100">
        <tr>
          <th
            class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            Name
          </th>
          <th
            class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            Token
          </th>
          <th
            class="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            Status
          </th>
          <th
            class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            Created
          </th>
          <th
            class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            Last used
          </th>
          <th class="px-3 py-2"></th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-100">
        @if (loading) {
          <tr>
            <td colspan="6" class="px-3 py-6 text-center text-gray-400">Loading…</td>
          </tr>
        } @else if (tokens.length === 0) {
          <tr>
            <td colspan="6" class="px-3 py-6 text-center text-gray-500">
              No personal tokens yet.
              <button class="link text-primary underline" (click)="startCreate()">
                Create your first one
              </button>
              .
            </td>
          </tr>
        } @else {
          @for (t of tokens; track t.id) {
            <tr class="hover:bg-gray-50" [class.opacity-60]="t.status !== 'active'">
              <td class="px-3 py-2 font-medium">{{ t.name }}</td>
              <td class="px-3 py-2 font-mono text-xs text-gray-600">{{ t.masked }}</td>
              <td class="px-3 py-2 text-center">
                <span class="badge badge-sm" [class]="statusClass(t.status)">{{ t.status }}</span>
              </td>
              <td class="px-3 py-2 text-xs text-gray-500">{{ t.created_at | date: 'short' }}</td>
              <td class="px-3 py-2 text-xs text-gray-500">
                @if (t.last_used_at) {
                  {{ t.last_used_at | date: 'short' }}
                } @else {
                  <span class="text-gray-400">Never used</span>
                }
              </td>
              <td class="px-3 py-2 whitespace-nowrap text-right">
                @if (t.status === 'active') {
                  <button
                    class="btn btn-xs btn-ghost"
                    (click)="revoke(t)"
                    [disabled]="busyId === t.id"
                    title="Stop this token working, but keep the record"
                  >
                    <i class="ri-forbid-line"></i>
                    Revoke
                  </button>
                }
                <button
                  class="btn btn-xs btn-error btn-outline"
                  (click)="remove(t)"
                  [disabled]="busyId === t.id"
                  title="Delete this token and its history permanently"
                >
                  <i class="ri-delete-bin-line"></i>
                  Delete
                </button>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>

  <p class="text-xs text-gray-400 mt-2">
    <b>Revoke</b>
    stops the token working immediately but keeps the row, so you can still see when it was last
    used.
    <b>Delete</b>
    also removes that record and cannot be undone. Tokens never expire on their own — they stay
    valid until you revoke or delete them, or until your admin account is deactivated.
  </p>
  <p class="text-xs text-gray-400 mt-1">
    A token can do everything you can in Tracer, with one exception: it cannot manage tokens. Only a
    signed-in session can create, revoke or delete them.
  </p>
</div>
